<template>
  <div class="d-flex flex-column flex-grow-1 rounded p-3">
    <BaseToolBar title="Matériels" subtitle="Liste des matériels enregistrés">
      <div class="btn-group mr-1" role="group">
        <BaseButton
          class="btn btn-primary ml-3"
          v-tooltip="'Exporter les matériels'"
          @click="exportMateriels"
          :loading="loading"
          text="Exporter (.xls)"
        ></BaseButton>
      </div>
      <BaseButton
        class="btn btn-primary"
        v-tooltip="'Ajouter un matériel'"
        @click="storeMateriel"
        :loading="loading"
        icon="plus"
        text="Ajouter"
      ></BaseButton>
    </BaseToolBar>

    <!-- Filtres -->
    <div
      class="mb-4 d-flex justify-content-between align-items-center flex-wrap"
    >
      <div class="d-flex align-items-center mb-2 flex-wrap">
        <div class="btn-group mr-3" role="group">
          <button
            type="button"
            class="btn"
            :class="{
              'btn-primary': currentFilter === 'suivi',
              'btn-outline-primary': currentFilter !== 'suivi'
            }"
            @click="currentFilter = 'suivi'"
          >
            Suivi
            <span class="badge badge-light ml-1">{{
              materielSuivi.length
            }}</span>
          </button>
          <button
            type="button"
            class="btn"
            :class="{
              'btn-primary': currentFilter === 'non-suivi',
              'btn-outline-primary': currentFilter !== 'non-suivi'
            }"
            @click="currentFilter = 'non-suivi'"
          >
            Non suivi
            <span class="badge badge-light ml-1">{{
              materielNonSuivi.length
            }}</span>
          </button>
        </div>

        <!-- Filtres existants -->
        <div class="form-group mb-0 mr-3">
          <select
            class="form-control form-control-sm"
            v-model="typeFilter"
            style="min-width: 150px;"
          >
            <option :value="null">Tous les types</option>
            <option
              v-for="type in data.materielTypes"
              :key="type.id"
              :value="type.id"
            >
              {{ type.description }}
            </option>
          </select>
        </div>

        <div class="form-group mb-0 mr-3">
          <select
            class="form-control form-control-sm"
            v-model="etatFilter"
            style="min-width: 130px;"
          >
            <option :value="null">Tous les états</option>
            <option
              v-for="etat in data.materielEtats"
              :key="etat.id"
              :value="etat.id"
            >
              {{ etat.description }}
            </option>
          </select>
        </div>

        <div class="form-group mb-0">
          <select
            class="form-control form-control-sm"
            v-model="societeFilter"
            style="min-width: 150px;"
          >
            <option :value="null">Toutes les sociétés</option>
            <option
              v-for="societe in data.societes"
              :key="societe.id"
              :value="societe.id"
            >
              {{ societe.description }}
            </option>
          </select>
        </div>
      </div>

      <div class="input-group" style="max-width: 300px;">
        <div class="input-group-prepend">
          <span class="input-group-text">
            <i class="fas fa-search"></i>
          </span>
        </div>
        <input
          type="text"
          class="form-control"
          placeholder="Rechercher..."
          v-model="searchQuery"
        />
      </div>
    </div>

    <!-- Tableau des matériels -->
    <div>
      <table class="table table-striped">
        <thead>
          <tr>
            <th>Identifiant</th>
            <th>Description</th>
            <th>Type</th>
            <th>État</th>
            <th>Affecté à</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="materiel in materielsFiltered"
            :key="materiel.id"
            :class="{
              'table-danger':
                materiel.materiel_collab_id && materiel.date_sortie
            }"
          >
            <td>{{ materiel.identifiant || "Non défini" }}</td>
            <td>{{ materiel.description || "Non définie" }}</td>
            <td>{{ getMaterielTypeDescription(materiel.materiel_type_id) }}</td>
            <td>
              <span
                class="badge"
                :class="getMaterielEtatClass(materiel.materiel_etat_id)"
              >
                {{ getMaterielEtatDescription(materiel.materiel_etat_id) }}
              </span>
            </td>
            <td>
              <span
                :class="{
                  'text-danger font-weight-bold':
                    materiel.materiel_collab_id && materiel.date_sortie,
                  'text-success':
                    materiel.materiel_collab_id &&
                    (!materiel.date_sortie || materiel.date_sortie === ''),
                  'text-muted': !materiel.materiel_collab_id
                }"
              >
                {{
                  materiel.materiel_collab_id
                    ? materiel.collaborateur_prenom +
                      " " +
                      materiel.collaborateur_nom
                    : "Non affecté"
                }}
              </span>
            </td>
            <td style="min-width: 200px;">
              <button
                class="btn btn-sm btn-primary mr-2"
                @click="editMateriel(materiel)"
              >
                Détails
              </button>
              <button
                class="btn btn-sm btn-danger"
                @click="confirmDelete(materiel)"
              >
                Supprimer
              </button>
            </td>
          </tr>
          <tr v-if="materielsFiltered.length === 0">
            <td colspan="6" class="text-center">
              Aucun matériel ne correspond aux critères de recherche
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Compteur -->
    <div class="d-flex justify-content-between align-items-center mt-3">
      <div>
        <span class="text-muted">
          {{ materielsFiltered.length }} matériel(s) sur {{ materiels.length }}
        </span>
      </div>
    </div>

    <!-- Modal détail matériel -->
    <div
      v-if="selectedMateriel"
      class="modal fade show d-block"
      tabindex="-1"
      style="background-color: rgba(0,0,0,0.5);"
    >
      <div class="modal-dialog modal-xxl">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              {{ selectedMateriel.identifiant || "Nouveau matériel" }}
            </h5>
            <button type="button" class="close" @click="closeModal">
              <span>&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <!-- Système d'onglets -->
            <ul class="nav nav-tabs mb-3">
              <li class="nav-item">
                <a
                  class="nav-link"
                  :class="{ active: activeTab === 'general' }"
                  href="#"
                  @click.prevent="activeTab = 'general'"
                >
                  Général
                </a>
              </li>
              <li class="nav-item">
                <a
                  class="nav-link"
                  :class="{ active: activeTab === 'affectations' }"
                  href="#"
                  @click.prevent="activeTab = 'affectations'"
                >
                  Affectations
                </a>
              </li>
              <li class="nav-item">
                <a
                  class="nav-link"
                  :class="{ active: activeTab === 'verifications' }"
                  href="#"
                  @click.prevent="activeTab = 'verifications'"
                >
                  Vérifications
                </a>
              </li>
            </ul>

            <!-- Indicateur de chargement -->
            <div v-if="loading && !selectedMateriel" class="text-center py-4">
              <div class="spinner-border text-primary" role="status">
                <span class="sr-only">Chargement...</span>
              </div>
              <p class="mt-2">Chargement du matériel...</p>
            </div>

            <!-- Contenu des onglets -->
            <div
              v-if="selectedMateriel"
              v-show="activeTab === 'general'"
              class="tab-content"
            >
              <materiel-general
                :materiel="selectedMateriel"
                :data="data"
                @update="onMaterielUpdate"
              ></materiel-general>
            </div>

            <div
              v-if="selectedMateriel"
              v-show="activeTab === 'affectations'"
              class="tab-content"
            >
              <materiel-affectations
                :materiel="selectedMateriel"
                :data="data"
                @update="onMaterielUpdate"
              ></materiel-affectations>
            </div>

            <div
              v-if="selectedMateriel"
              v-show="activeTab === 'verifications'"
              class="tab-content"
            >
              <materiel-verifications
                :materiel="selectedMateriel"
                :data="data"
                @update="onMaterielUpdate"
              ></materiel-verifications>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeModal">
              Fermer
            </button>
            <button type="button" class="btn btn-primary" @click="saveMateriel">
              Enregistrer
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapMultiRowFields } from "vuex-map-fields";
import { mapGetters } from "vuex";
import { mapActions } from "vuex";
import BaseButton from "@/components/bases/Button.vue";
import BaseToolBar from "@/components/bases/ToolBar.vue";
import MaterielGeneral from "@/components/materiels/MaterielGeneral.vue";
import MaterielAffectations from "@/components/materiels/MaterielAffectations.vue";
import MaterielVerifications from "@/components/materiels/MaterielVerifications.vue";

export default {
  components: {
    BaseToolBar,
    BaseButton,
    MaterielGeneral,
    MaterielAffectations,
    MaterielVerifications
  },

  data() {
    return {
      loading: false,
      feedback: {},
      typeFilter: null,
      etatFilter: null,
      societeFilter: null,
      currentFilter: "suivi",
      searchQuery: "",
      selectedMateriel: null,
      activeTab: "general"
    };
  },

  computed: {
    ...mapGetters({ data: "materiel/data" }),
    ...mapMultiRowFields("materiel", ["materiels"]),
    materielSuivi() {
      return this.materiels.filter(m => m.suivi === 1);
    },
    materielNonSuivi() {
      return this.materiels.filter(m => m.suivi === 0 || m.suivi === null);
    },
    materielsFiltered() {
      let mats = this.materiels;

      // Filtre suivi/non suivi
      if (this.currentFilter === "suivi") {
        mats = mats.filter(m => m.suivi === 1);
      } else if (this.currentFilter === "non-suivi") {
        mats = mats.filter(m => m.suivi === 0 || m.suivi === null);
      }

      // Filtres par type, état, société
      if (this.typeFilter) {
        mats = mats.filter(m => m.materiel_type_id == this.typeFilter);
      }
      if (this.etatFilter) {
        mats = mats.filter(m => m.materiel_etat_id == this.etatFilter);
      }
      if (this.societeFilter) {
        mats = mats.filter(m => m.materiel_societe_id == this.societeFilter);
      }

      // Filtre de recherche
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        mats = mats.filter(
          m =>
            (m.identifiant && m.identifiant.toLowerCase().includes(query)) ||
            (m.description && m.description.toLowerCase().includes(query)) ||
            (m.num_serie && m.num_serie.toLowerCase().includes(query)) ||
            (m.marque && m.marque.toLowerCase().includes(query)) ||
            (m.modele && m.modele.toLowerCase().includes(query)) ||
            (m.collaborateur_nom &&
              m.collaborateur_nom.toLowerCase().includes(query)) ||
            (m.collaborateur_prenom &&
              m.collaborateur_prenom.toLowerCase().includes(query))
        );
      }

      // Trier par ID décroissant (les nouveaux matériels en premier)
      return mats.sort((a, b) => {
        // Si les deux ont un ID, trier par ID décroissant
        if (a.id && b.id) {
          return b.id - a.id;
        }
        // Sinon, mettre les éléments sans ID en premier (nouveaux non encore sauvegardés)
        if (!a.id && b.id) return -1;
        if (a.id && !b.id) return 1;
        return 0;
      });
    }
  },

  methods: {
    ...mapActions({
      _getAll: "materiel/getAll",
      _storeMateriel: "materiel/createMateriel",
      _exportMateriels: "materiel/exportMateriels",
      _updateMateriel: "materiel/updateMateriel",
      _deleteMateriel: "materiel/deleteMateriel",
      _getMateriel: "materiel/getMateriel"
    }),

    sort(field) {
      if (this.sortBy === field) {
        this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      } else {
        this.sortBy = field;
        this.sortOrder = "asc";
      }
    },

    nouveauMateriel() {
      this.selectedMaterielId = null;
      this.$nextTick(() => {
        window.$("#materielModal").modal("show");
      });
    },

    editMateriel: function(materiel) {
      console.log("Chargement du matériel ID:", materiel.id);
      this.loading = true;
      this.selectedMateriel = null;
      this.activeTab = "general";

      this._getMateriel(materiel.id)
        .then(response => {
          console.log("Matériel chargé depuis l'API:", response.data);
          this.selectedMateriel = { ...response.data };
        })
        .catch(error => {
          console.error("Erreur lors du chargement du matériel:", error);
          this.$toast.error("Erreur lors du chargement du matériel");
        })
        .finally(() => {
          this.loading = false;
        });
    },

    closeModal: function() {
      this.selectedMateriel = null;
      this.activeTab = "general";
    },

    storeMateriel: function() {
      this.feedback = {};
      this.loading = true;
      this._storeMateriel({ materiel_new: true })
        .then(response => {
          this.editMateriel(response.data);
        })
        .finally(() => (this.loading = false));
    },

    exportMateriels: function() {
      this.loading = true;
      this._exportMateriels().finally(() => (this.loading = false));
    },

    saveMateriel: function() {
      this.loading = true;
      this._updateMateriel(this.selectedMateriel)
        .then(() => {
          // Recharger les données pour s'assurer que la liste est à jour
          return this._getAll();
        })
        .then(() => {
          this.closeModal();
          this.$toast.success("Matériel sauvegardé avec succès");
        })
        .catch(error => {
          console.error("Erreur lors de la sauvegarde:", error);
          this.$toast.error("Erreur lors de la sauvegarde");
        })
        .finally(() => (this.loading = false));
    },

    confirmDelete: function(materiel) {
      if (confirm("Êtes-vous sûr de vouloir supprimer ce matériel ?")) {
        this._deleteMateriel(materiel)
          .then(() => {
            this.$toast.success("Matériel supprimé avec succès");
          })
          .catch(() => {
            this.$toast.error("Erreur lors de la suppression");
          });
      }
    },

    onMaterielUpdate: function(materiel) {
      console.log("onMaterielUpdate appelé avec:", materiel);
      this.selectedMateriel = { ...materiel };

      // Ne pas mettre à jour la liste pour éviter de perdre les champs collaborateur_nom et collaborateur_prenom
      // La liste sera mise à jour lors de la sauvegarde ou du rechargement depuis l'API
    },

    getMaterielTypeDescription: function(id) {
      if (!this.data.materielTypes) return "Non défini";
      const type = this.data.materielTypes.find(t => t.id === id);
      return type ? type.description : "Non défini";
    },

    getMaterielEtatDescription: function(id) {
      if (!this.data.materielEtats) return "Non défini";
      const etat = this.data.materielEtats.find(e => e.id === id);
      return etat ? etat.description : "Non défini";
    },

    getMaterielEtatClass: function(id) {
      if (!this.data.materielEtats) return "badge-secondary";
      const etat = this.data.materielEtats.find(e => e.id === id);
      if (!etat) return "badge-secondary";

      // Classes de badges selon l'état
      switch (etat.description.toLowerCase()) {
        case "en service":
        case "disponible":
          return "badge-success";
        case "en panne":
        case "défaillant":
          return "badge-danger";
        case "en maintenance":
        case "en réparation":
          return "badge-warning";
        case "retiré":
        case "obsolète":
          return "badge-secondary";
        default:
          return "badge-info";
      }
    },

    getMaterielCollaborateurName: function(id) {
      if (!id || !this.data.users) return null;
      const user = this.data.users.find(u => u.id === id);
      return user
        ? `${user.collaborateur_nom} ${user.collaborateur_prenom}`
        : null;
    },

    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("fr-FR");
    },

    isVerificationSoon(dateString) {
      if (!dateString) return false;
      const date = new Date(dateString);
      const today = new Date();
      const diffDays = Math.ceil((date - today) / (1000 * 60 * 60 * 24));
      return diffDays <= 30 && diffDays > 0;
    },

    isVerificationOverdue(dateString) {
      if (!dateString) return false;
      const date = new Date(dateString);
      const today = new Date();
      return date < today;
    }
  },

  async mounted() {
    this.loading = true;
    try {
      // Charger toutes les données (types, états, sociétés, fournisseurs, etc.)
      await this._getAll();
    } catch (error) {
      console.error("Erreur lors du chargement des données:", error);
      this.$toast.error("Erreur lors du chargement des données");
    } finally {
      this.loading = false;
    }
  }
};
</script>
<style lang="css">
.modal-xxl {
  max-width: 95vw;
}

/* Amélioration responsive pour les petits écrans */
@media (max-width: 768px) {
  .modal-xxl {
    max-width: 98vw;
    margin: 0.5rem;
  }
}
</style>
